import axios from 'axios';
import { IInvoice, IInvoicesResponse, IInvoiceResponse, IInvoicePage, IInvoicePagesResponse, IInvoicePageResponse, IInvoiceCreate, IInvoiceUpdate, IInvoicePageCreate, IInvoicePageUpdate } from '../types/invoice';
import { IInvoiceItemExtended, IInvoiceCreateExtended } from '../types/invoiceExtended';

// Create a dedicated axios instance just for invoice operations
// This will ensure consistent handling and avoid conflicts with other interceptors
const invoiceAxios = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  }
});

// Constants for authentication and consistent data structure
const MOCK_USER_ID = "64067927a9259bbde06c6f15";
// Always use the active branch from auth middleware - don't hardcode branch ID here

// Custom error handling
const handleInvoiceError = (error: any) => {
  console.error('Invoice API Error:', error);
  
  if (error.response) {
    console.error('Response status:', error.response.status);
    console.error('Response data:', error.response.data);
    
    // Provide a more useful error message
    const message = error.response.data?.message ||
                    error.response.data?.error ||
                    `Error: ${error.response.status}`;
    throw new Error(`حدث خطأ أثناء حفظ الفاتورة: ${message}`);
  }
  
  throw error;
};

// Invoice Pages API calls
export const getInvoicePages = async (): Promise<IInvoicePage[]> => {
  const response = await axios.get<IInvoicePagesResponse>('/api/invoices/pages');
  return response.data.data.pages;
};

export const getInvoicePageById = async (id: string): Promise<IInvoicePage> => {
  const response = await axios.get<IInvoicePageResponse>(`/api/invoices/pages/${id}`);
  return response.data.data.page;
};

export const createInvoicePage = async (page: IInvoicePageCreate): Promise<IInvoicePage> => {
  console.log('إنشاء صفحة فاتورة جديدة بالبيانات:', JSON.stringify(page, null, 2));
  
  try {
    // استخدام axios مع رؤوس إضافية للتوثيق
    const response = await axios.post('/api/invoices/pages', page, {
      headers: {
        'X-User-ID': MOCK_USER_ID,
        'X-Branch-ID': localStorage.getItem('currentBranchId') || ''
      }
    });
    
    console.log('تم إنشاء صفحة الفاتورة بنجاح:', response.data);
    return response.data.data.page;
  } catch (error: any) {
    console.error('خطأ في خدمة إنشاء صفحة الفاتورة:', error);
    
    // تعزيز رسائل الخطأ
    if (error.response) {
      console.error('رد خطأ من الخادم:', error.response.data);
      throw new Error(`خطأ من الخادم: ${error.response.status} - ${error.response.data?.message || 'خطأ غير معروف'}`);
    }
    
    throw error;
  }
};

export const updateInvoicePage = async (id: string, page: IInvoicePageUpdate): Promise<IInvoicePage> => {
  console.log('تحديث صفحة الفاتورة بالبيانات:', JSON.stringify(page, null, 2));
  
  try {
    // استخدام axios مع رؤوس إضافية للتوثيق
    const response = await axios.patch(`/api/invoices/pages/${id}`, page, {
      headers: {
        'X-User-ID': MOCK_USER_ID,
        'X-Branch-ID': localStorage.getItem('currentBranchId') || ''
      }
    });
    
    console.log('تم تحديث صفحة الفاتورة بنجاح:', response.data);
    return response.data.data.page;
  } catch (error: any) {
    console.error('خطأ في خدمة تحديث صفحة الفاتورة:', error);
    
    // تعزيز رسائل الخطأ
    if (error.response) {
      console.error('رد خطأ من الخادم:', error.response.data);
      throw new Error(`خطأ من الخادم: ${error.response.status} - ${error.response.data?.message || 'خطأ غير معروف'}`);
    }
    
    throw error;
  }
};

export const deleteInvoicePage = async (id: string): Promise<void> => {
  try {
    await axios.delete(`/api/invoices/pages/${id}`, {
      headers: {
        'X-User-ID': MOCK_USER_ID,
        'X-Branch-ID': localStorage.getItem('currentBranchId') || ''
      }
    });
    console.log(`تم حذف صفحة الفاتورة ${id} بنجاح`);
  } catch (error: any) {
    console.error(`خطأ في حذف صفحة الفاتورة ${id}:`, error);
    
    if (error.response) {
      throw new Error(`خطأ من الخادم: ${error.response.status} - ${error.response.data?.message || 'خطأ غير معروف'}`);
    }
    
    throw error;
  }
};

// Invoices API calls
export const getInvoices = async (
  pageId?: string,
  status?: string,
  startDate?: string,
  endDate?: string,
  customerId?: string,
  supplierId?: string,
  page = 1,
  limit = 10,
  searchTerm?: string
): Promise<{ invoices: IInvoice[]; pagination: { total: number; page: number; pages: number } }> => {
  const params = new URLSearchParams();
  
  if (pageId) params.append('pageId', pageId);
  if (status) params.append('status', status);
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (customerId) params.append('customerId', customerId);
  if (supplierId) params.append('supplierId', supplierId);
  if (searchTerm) params.append('searchTerm', searchTerm);
  params.append('page', page.toString());
  params.append('limit', limit.toString());
  
  const response = await axios.get<IInvoicesResponse>(`/api/invoices?${params.toString()}`);
  return response.data.data;
};

// دالة جديدة لجلب الفواتير حسب النوع للتقارير
export const getInvoicesByType = async (
  type: string,
  filters: {
    startDate?: string;
    endDate?: string;
    branchId?: string;
    branchIds?: string[];
    status?: string;
  }
): Promise<{ invoices: IInvoice[]; total: number }> => {
  const params = new URLSearchParams();
  
  params.append('type', type);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  
  // دعم الفروع المتعددة
  if (filters.branchIds && filters.branchIds.length > 0 && !filters.branchIds.includes('all')) {
    params.append('branchIds', filters.branchIds.join(','));
  } else if (filters.branchId && filters.branchId !== 'all') {
    params.append('branchId', filters.branchId);
  }
  
  if (filters.status) params.append('status', filters.status);
  params.append('limit', '1000'); // جلب عدد كبير للتقارير
  
  const response = await axios.get<IInvoicesResponse>(`/api/invoices/reports?${params.toString()}`);
  return {
    invoices: response.data.data.invoices,
    total: response.data.data.pagination.total
  };
};

export const getInvoiceById = async (id: string): Promise<IInvoice> => {
  try {
    // Basic client-side validation to prevent obvious errors
    if (!id || id === 'undefined' || id === 'null' || id.length !== 24) {
      throw new Error('معرف فاتورة غير صالح - الرجاء التحقق من الرابط');
    }
    
    const response = await axios.get<IInvoiceResponse>(`/api/invoices/${id}`);
    return response.data.data.invoice;
  } catch (error: any) {
    // Enhanced error handling
    console.error('Error fetching invoice:', error);
    if (error.response?.status === 400 && error.response?.data?.message?.includes('معرف فاتورة غير صالح')) {
      throw new Error('معرف الفاتورة غير صالح - الرجاء التحقق من الرابط');
    }
    throw error;
  }
};

export const createInvoice = async (invoice: IInvoiceCreate | IInvoiceCreateExtended): Promise<IInvoice> => {
  try {
    // تسجيل بيانات الفاتورة للتتبع
    console.log('إنشاء فاتورة جديدة بالبيانات التالية:', {
      pageId: invoice.pageId,
      // مهم جداً: معرف الفرع مطلوب لنظام ترقيم الفواتير الجديد [معرف الفرع]-[معرف صفحة الفاتورة]-[رقم تسلسلي]
      branchId: invoice.branchId || 'غير محدد',
      invoiceNumber: invoice.invoiceNumber,
      itemsCount: invoice.items?.length || 0
    });
    
    // إنشاء كائن البيانات الضرورية للفاتورة
    let requiredInvoiceData: any;
    
    // تحويل invoice إلى IInvoiceCreateExtended للوصول إلى حقول الكوبونات
    const extInvoice = invoice as IInvoiceCreateExtended;
    
    requiredInvoiceData = {
      // الحقول المطلوبة مع القيم الافتراضية
      pageId: invoice.pageId,
      invoiceNumber: invoice.invoiceNumber || `INV-${Date.now().toString().substring(9)}`,
      date: invoice.date ? (() => {
        // تعديل فارق التوقيت: datetime-local يعطي وقت محلي، نحتاج تعديله قبل إرساله
        const localDate = new Date(invoice.date);
        const timezoneOffset = localDate.getTimezoneOffset() * 60000; // بالميلي ثانية
        const adjustedDate = new Date(localDate.getTime() - timezoneOffset);
        return adjustedDate.toISOString();
      })() : new Date().toISOString(),
      status: invoice.status || 'draft',
      
      // Items with proper number conversion and variant information
      items: (invoice.items || []).map(item => ({
        productId: item.productId,
        productName: item.productName,
        quantity: Number(item.quantity) || 1,
        price: Number(item.price) || 0,
        discount: Number(item.discount) || 0,
        // إضافة نوع الخصم للمنتج - مهم جداً لحفظ نوع الخصم (نسبة أو ثابت)
        discountType: item.discountType || 'fixed',
        tax: Number(item.tax) || 0,
        total: Number(item.total) || 0,
        // إجمالي المنتج الأصلي (السعر × الكمية) قبل أي خصومات
        ...(item.originalTotal !== undefined ? { originalTotal: Number(item.originalTotal) } : {}),
        // Variant information - critical for inventory tracking
        ...(item.variantId ? { variantId: item.variantId } : {}),
        ...(item.barcodeId ? { barcodeId: item.barcodeId } : {}),
        // Optional item fields
        ...(item.barcode ? { barcode: item.barcode } : {}),
        ...(item.unitId ? { unitId: item.unitId } : {}),
        ...(item.unitName ? { unitName: item.unitName } : {}),
        ...(item.unit ? { unit: item.unit } : {}),
        // Service product flag - important for service product operations
        ...(item.isServiceProduct ? { isServiceProduct: item.isServiceProduct } : {}),
        // Include product attributes if available
        ...(item.attributes && Object.keys(item.attributes).length > 0 ? { attributes: item.attributes } : {})
      })),
      
      // Financial info
      subtotal: Number(invoice.subtotal) || 0,
      tax: Number(invoice.tax) || 0,
      discount: Number(invoice.discount) || 0,
      // إضافة نوع الخصم (discountType) - نقطة مهمة جداً للتصحيح
      discountType: invoice.discountType || 'fixed', // حفظ نوع الخصم سواء كان ثابت أو نسبة مئوية
      total: Number(invoice.total) || 0,
      
      // Optional fields
      ...(invoice.customerId ? { customerId: invoice.customerId } : {}),
      ...(invoice.customerName ? { customerName: invoice.customerName } : {}),
      ...(invoice.supplierId ? { supplierId: invoice.supplierId } : {}),
      ...(invoice.supplierName ? { supplierName: invoice.supplierName } : {}),
      ...(invoice.warehouseId ? { warehouseId: invoice.warehouseId } : {}),
      ...(invoice.notes ? { notes: invoice.notes } : {}),
      // طرق الدفع - إضافة طرق الدفع إلى الفاتورة إذا كانت موجودة
      ...(Array.isArray(invoice.paymentMethods) ? { paymentMethods: invoice.paymentMethods } : {}),
      // مهم جداً: معرف الفرع (branchId) مطلوب لنظام ترقيم الفواتير الجديد [معرف الفرع]-[معرف صفحة الفاتورة]-[رقم تسلسلي]
      // إذا لم يتم توفير معرف الفرع، لن يعمل نظام ترقيم الفواتير بشكل صحيح
      // لا نرسل branchId إذا كان فارغاً - دع الخادم يستخدم الفرع النشط للمستخدم
      ...(invoice.branchId ? { branchId: invoice.branchId } : {}),
      // إضافة الحقول الجديدة للدفع الجزئي والفاتورة المصدر
      ...(invoice.sourceInvoiceId ? { sourceInvoiceId: invoice.sourceInvoiceId } : {}),
      ...(invoice.sourceInvoiceNumber ? { sourceInvoiceNumber: invoice.sourceInvoiceNumber } : {}),
      ...(invoice.cashPaidAmount !== undefined ? { cashPaidAmount: Number(invoice.cashPaidAmount) } : {}),
      ...(invoice.isPartialPayment !== undefined ? { isPartialPayment: invoice.isPartialPayment } : {}),
      // ============================================
      // إضافة حقول الكوبونات - مهمة جداً لنظام تخزين الكوبونات
      // ============================================
      ...(extInvoice.couponCodes && extInvoice.couponCodes.length > 0 ? { couponCodes: extInvoice.couponCodes } : {}),
      ...(extInvoice.couponDiscount !== undefined ? { couponDiscount: Number(extInvoice.couponDiscount) } : {}),
      ...(extInvoice.couponDiscountType ? { couponDiscountType: extInvoice.couponDiscountType } : {}),
      ...(extInvoice.appliedCouponDiscount !== undefined ? { appliedCouponDiscount: Number(extInvoice.appliedCouponDiscount) } : {}),
      ...(extInvoice.marketingEnabled !== undefined ? { marketingEnabled: extInvoice.marketingEnabled } : {}),
      ...(Array.isArray(extInvoice.availableCoupons) ? { availableCoupons: extInvoice.availableCoupons } : {}),
      createdBy: MOCK_USER_ID
    };
    // استخدام axios بدلاً من fetch للاستفادة من رؤوس التوثيق المضبوطة مسبقًا
    const response = await axios.post('/api/invoices', requiredInvoiceData, {
      headers: {
        'X-User-ID': MOCK_USER_ID,
        // لا نرسل X-Branch-ID إذا كان فارغاً - دع الخادم يستخدم الفرع النشط للمستخدم
        ...(invoice.branchId ? { 'X-Branch-ID': invoice.branchId } : {})
      }
    });
    
    // استخدام البيانات من response.data بدلاً من response.json()
    const data = response.data;
    console.log('تم إنشاء الفاتورة بنجاح:', {
      id: data.data.invoice._id,
      invoiceNumber: data.data.invoice.invoiceNumber,
      branchId: data.data.invoice.branchId,
      branchName: data.data.invoice.branchName
    });
    return data.data.invoice;
  } catch (error: any) {
    console.error('خطأ في خدمة إنشاء الفاتورة:', error);
    
    // طباعة تفاصيل الخطأ للتشخيص
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      console.error('Response headers:', error.response.headers);
    }
    
    throw error;
  }
};

export const updateInvoice = async (id: string, invoice: IInvoiceUpdate | Partial<IInvoiceCreateExtended>): Promise<IInvoice> => {
  try {
    // تحويل invoice إلى IInvoiceCreateExtended للوصول إلى حقول الكوبونات
    const extInvoice = invoice as IInvoiceCreateExtended;
    
    // Prepare update data, keeping it minimal to avoid unexpected side effects
    const updateData = {
      // Optional fields that might be updated
      ...(invoice.pageId ? { pageId: invoice.pageId } : {}),
      ...(invoice.invoiceNumber ? { invoiceNumber: invoice.invoiceNumber } : {}),
      ...(invoice.date ? {
        date: (() => {
          // تعديل فارق التوقيت: datetime-local يعطي وقت محلي، نحتاج تعديله قبل إرساله
          const localDate = new Date(invoice.date);
          const timezoneOffset = localDate.getTimezoneOffset() * 60000;
          const adjustedDate = new Date(localDate.getTime() - timezoneOffset);
          return adjustedDate.toISOString();
        })()
      } : {}),
      ...(invoice.status ? { status: invoice.status } : {}),
      
      // Handle items if present
      ...(Array.isArray(invoice.items) ? {
        items: invoice.items.map(item => {
          // استخراج البيانات الأساسية
          const price = Number(item.price) || 0;
          const quantity = Number(item.quantity) || 1;
          const taxRate = Number(item.tax) || 0;
          const itemDiscount = Number(item.discount) || 0;
          const total = Number(item.total) || 0;
          const itemTotal = price * quantity;
          
          // التعامل مع الخصم الكلي للفاتورة إذا كان موجوداً
          let effectiveDiscount = itemDiscount;
          
          // إذا كان هناك خصم كلي على الفاتورة
          if (Number(invoice.discount) > 0 && invoice.discountType) {
            // حساب حصة المنتج من الخصم الكلي للفاتورة
            const totalInvoiceBeforeDiscount = Number(invoice.total) + Number(invoice.discount);
            
            if (invoice.discountType === 'percentage') {
              // إذا كان الخصم نسبة مئوية
              const discountPercentage = Number(invoice.discount);
              // حصة المنتج من الخصم النسبي = (سعر المنتج × الكمية) × نسبة الخصم ÷ 100
              const itemDiscountShare = (price * quantity) * (discountPercentage / 100);
              effectiveDiscount = (itemDiscount + (itemDiscountShare / quantity));
            } else {
              // إذا كان الخصم ثابت (fixed)
              if (totalInvoiceBeforeDiscount > 0) {
                // حصة المنتج من الخصم الثابت = قيمة الخصم الكلي × (سعر المنتج × الكمية ÷ إجمالي الفاتورة قبل الخصم)
                const itemRatio = (price * quantity) / totalInvoiceBeforeDiscount;
                const itemDiscountShare = Number(invoice.discount) * itemRatio;
                effectiveDiscount = (itemDiscount + (itemDiscountShare / quantity));
              }
            }
          }
          
          // حساب السعر بعد تطبيق الخصم أولاً (الخصم الفردي + حصة الخصم الكلي)
          const priceAfterDiscount = price - effectiveDiscount;
          
          // حساب السعر قبل الضريبة وقيمة الضريبة بناءً على السعر بعد الخصم
          let preTaxPrice = priceAfterDiscount;
          let taxAmount = 0;
          
          if (taxRate > 0) {
            // السعر قبل الضريبة للوحدة = السعر بعد الخصم ÷ (1 + نسبة الضريبة)
            preTaxPrice = priceAfterDiscount / (1 + (taxRate / 100));
            // قيمة الضريبة للوحدة الواحدة بعد تطبيق الخصم
            taxAmount = priceAfterDiscount - preTaxPrice;
            
            // تحويل السعر قبل الضريبة وقيمة الضريبة إلى المجموع الكلي (× الكمية)
            // لعرضها بشكل صحيح في الفاتورة المطبوعة
            preTaxPrice = preTaxPrice * quantity;
            taxAmount = taxAmount * quantity;
          }
          
          return {
            productId: item.productId,
            productName: item.productName,
            quantity: Number(item.quantity) || 1,
            price: price,
            discount: Number(item.discount) || 0,
            // إضافة نوع الخصم للمنتج - مهم جداً لحفظ نوع الخصم (نسبة أو ثابت)
            discountType: item.discountType || 'fixed',
            tax: taxRate,
            // تحديث الإجمالي ليعكس السعر بعد الخصم × الكمية
            total: priceAfterDiscount * quantity,
            // إجمالي المنتج الأصلي (السعر × الكمية) قبل أي خصومات
            originalTotal: item.originalTotal !== undefined ? Number(item.originalTotal) : price * quantity,
            // تخزين السعر قبل الضريبة وقيمة الضريبة
            preTaxPrice: preTaxPrice,
            taxAmount: taxAmount,
            // تخزين الخصم الفعلي (الخصم الفردي + حصة الخصم الكلي) للعرض في الفاتورة
            effectiveDiscount: effectiveDiscount * quantity,
            // Variant information - critical for inventory tracking
            // دائماً تضمين variantId
            variantId: item.variantId,
            // اضافة variantName - مهم جدا لحفظ معلومات المتغيرات والخصائص
            // دائماً تضمين variantName حتى لو كانت القيمة فارغة لضمان معالجة الخادم لها
            variantName: item.variantName || null,
            barcodeId: item.barcodeId,
            // Optional item fields
            barcode: item.barcode,
            unitId: item.unitId,
            unitName: item.unitName,
            unit: item.unit,
            // Product attributes - critical for item identification
            attributes: item.attributes || {},
            // Variant attributes - needed for proper product variant tracking
            // دائماً تضمين variantAttributes حتى لو كانت القيمة فارغة لضمان معالجة الخادم لها
            variantAttributes: item.variantAttributes || {}
          };
        })
      } : {}),
      
      // Financial fields - always include essential fields with explicit values
      // تضمين كل الحقول المالية الأساسية بشكل صريح في طلب التحديث
      // عند التعديل يجب أن نحسب المجموع الفرعي كمجموع قبل الضريبة وليس الإجمالي
      // عند التعديل يجب أن نحسب المجموع الفرعي كمجموع قبل الضريبة وليس الإجمالي
      subtotal: Number(invoice.tax) > 0 ? Number(invoice.total) - Number(invoice.tax) : invoice.total,
      // استخدم نفس القيمة التي حسبناها للـ subtotal لتكون هي الـ subtotalBeforeTax
      subtotalBeforeTax: Number(invoice.tax) > 0 ? Number(invoice.total) - Number(invoice.tax) : invoice.total,
      // تضمين وضع الضريبة لضمان معالجتها بشكل صحيح
      taxEnabled: true,
      ...(typeof invoice.tax !== 'undefined' ? { tax: Number(invoice.tax) } : {}),
      ...(typeof invoice.discount !== 'undefined' ? { discount: Number(invoice.discount) } : {}),
      // إضافة نوع الخصم (discountType) عند تحديث الفاتورة - نقطة مهمة جداً للتصحيح
      ...(invoice.discountType ? { discountType: invoice.discountType } : {}),
      ...(typeof invoice.total !== 'undefined' ? { total: Number(invoice.total) } : {}),
      
      // Optional reference fields
      ...(invoice.customerId ? { customerId: invoice.customerId } : {}),
      ...(invoice.customerName ? { customerName: invoice.customerName } : {}),
      ...(invoice.supplierId ? { supplierId: invoice.supplierId } : {}),
      ...(invoice.supplierName ? { supplierName: invoice.supplierName } : {}),
      ...(invoice.warehouseId ? { warehouseId: invoice.warehouseId } : {}),
      ...(invoice.notes !== undefined ? { notes: invoice.notes } : {}),
      
      // طرق الدفع - إضافة طرق الدفع إلى الفاتورة إذا كانت موجودة
      ...(Array.isArray(invoice.paymentMethods) ? { paymentMethods: invoice.paymentMethods } : {}),
      
      // مهم جداً: معرف الفرع مطلوب لنظام ترقيم الفواتير الجديد [معرف الفرع]-[معرف صفحة الفاتورة]-[رقم تسلسلي]
      branchId: invoice.branchId || '',
      // إضافة الحقول الجديدة للدفع الجزئي والفاتورة المصدر
      ...(invoice.sourceInvoiceId ? { sourceInvoiceId: invoice.sourceInvoiceId } : {}),
      ...(invoice.cashPaidAmount !== undefined ? { cashPaidAmount: Number(invoice.cashPaidAmount) } : {}),
      ...(invoice.isPartialPayment !== undefined ? { isPartialPayment: invoice.isPartialPayment } : {}),
      // ============================================
      // إضافة حقول الكوبونات - مهمة جداً لنظام تخزين الكوبونات
      // ============================================
      ...(extInvoice.couponCodes && extInvoice.couponCodes.length > 0 ? { couponCodes: extInvoice.couponCodes } : {}),
      ...(extInvoice.couponDiscount !== undefined ? { couponDiscount: Number(extInvoice.couponDiscount) } : {}),
      ...(extInvoice.couponDiscountType ? { couponDiscountType: extInvoice.couponDiscountType } : {}),
      ...(extInvoice.appliedCouponDiscount !== undefined ? { appliedCouponDiscount: Number(extInvoice.appliedCouponDiscount) } : {}),
      ...(extInvoice.marketingEnabled !== undefined ? { marketingEnabled: extInvoice.marketingEnabled } : {}),
      ...(Array.isArray(extInvoice.availableCoupons) ? { availableCoupons: extInvoice.availableCoupons } : {}),
      updatedAt: new Date().toISOString() // Explicitly set update timestamp
    };
    
    console.log(`Updating invoice ${id} with data:`, JSON.stringify(updateData, null, 2));
    
    // استخدام axios بدلاً من fetch للاستفادة من رؤوس التوثيق المضبوطة مسبقًا
    const response = await axios.patch(`/api/invoices/${id}`, updateData, {
      headers: {
        'X-User-ID': MOCK_USER_ID,
        'X-Branch-ID': invoice.branchId || ''
      }
    });
    
    // استخدام البيانات من response.data بدلاً من response.json()
    const data = response.data;
    return data.data.invoice;
  } catch (error) {
    console.error('Error in updateInvoice service:', error);
    throw error;
  }
};

export const deleteInvoice = async (id: string): Promise<void> => {
  await axios.delete(`/api/invoices/${id}`);
};

// Export functions
export const exportInvoiceToPdf = (id: string): void => {
  window.open(`/api/invoices/${id}/export/pdf`, '_blank');
};

export const exportInvoiceToExcel = (id: string): void => {
  window.open(`/api/invoices/${id}/export/excel`, '_blank');
};

// Print function
export const printInvoice = (id: string): void => {
  window.open(`/api/invoices/${id}/print`, '_blank');
};

// البحث عن فاتورة بالرقم
export const searchInvoiceByNumber = async (invoiceNumber: string): Promise<IInvoiceResponse> => {
  try {
    if (!invoiceNumber.trim()) {
      throw new Error('رقم الفاتورة مطلوب للبحث');
    }
    
    // استخدام مسار getInvoices مع فلتر invoiceNumber
    const params = new URLSearchParams();
    params.append('invoiceNumber', invoiceNumber);
    
    const response = await axios.get<IInvoicesResponse>(`/api/invoices?${params.toString()}`);
    
    // إذا وجدت فواتير، أرجع الأولى
    if (response.data.data.invoices && response.data.data.invoices.length > 0) {
      return {
        success: true,
        data: {
          invoice: response.data.data.invoices[0]
        }
      };
    } else {
      throw new Error('لم يتم العثور على الفاتورة');
    }
  } catch (error: any) {
    console.error('Error searching invoice by number:', error);
    throw error;
  }
};